import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Contact,
  PawPrint,
  Package,
  Tags,
  FolderTree,
  MessageSquare,
  Store as StoreIcon,
  BookOpen,
  CornerDownLeft,
  Loader2
} from 'lucide-react';
import clsx from 'clsx';

import { searchApi } from '../api/search.js';

const TYPE_ICON = {
  customer: Contact,
  pet: PawPrint,
  product: Package,
  brand: Tags,
  category: FolderTree,
  consultation: MessageSquare,
  store: StoreIcon,
  knowledge: BookOpen
};

/**
 * Resolve the deep-link for a result entity. Catalogue and system records live
 * only under the admin surface; customer-bound records (customers, pets,
 * consultations) follow the layout `basePath` so the component can be embedded
 * in either the admin or employee shell.
 */
function resolveHref(entity, basePath) {
  if (!entity) return basePath;
  switch (entity.type) {
    case 'customer':
      return `${basePath}/customers/${entity.id}`;
    case 'pet':
    case 'consultation':
      return entity.customerId ? `${basePath}/customers/${entity.customerId}` : `${basePath}/customers`;
    case 'product':
      // The employee shell has no product editor: land on the inventory
      // filtered to this product by SKU. Admin opens the full editor.
      if (basePath === '/employee') {
        return entity.sku ? `/employee/inventory?sku=${encodeURIComponent(entity.sku)}` : '/employee/inventory';
      }
      return `/admin/products/${entity.id}/edit`;
    case 'brand':
      return '/admin/brands';
    case 'category':
      return '/admin/categories';
    case 'store':
      return '/admin/stores';
    case 'knowledge':
      // Sellers read the document in the employee knowledge base; admins manage
      // it in the RAG admin page.
      if (basePath === '/employee') {
        return entity.id ? `/employee/knowledge?doc=${encodeURIComponent(entity.id)}` : '/employee/knowledge';
      }
      return '/admin/rag';
    default:
      return basePath;
  }
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Global search command palette. A compact trigger in the navbar opens a
 * centered overlay with a single query field; results stream in grouped by
 * provenance. Keyboard: Cmd/Ctrl+K opens, Esc closes, Up/Down move, Enter
 * navigates to the highlighted result.
 */
export function GlobalSearch({ basePath = '/admin', variant = 'default', types, ownStoreIds }) {
  const ownStores = useMemo(() => new Set((ownStoreIds || []).map(String)), [ownStoreIds]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const debounced = useDebounced(term.trim(), 250);
  const enabled = open && debounced.length >= 2;

  // Restrict the federated search to provenances that have a destination in
  // the current surface (e.g. the employee shell can only open customer-bound
  // records). Stable key for caching.
  const typesKey = Array.isArray(types) && types.length ? types.join(',') : 'all';

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', typesKey, debounced],
    queryFn: () => searchApi.global({ q: debounced, types: types && types.length ? types : undefined }),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev
  });

  const groups = useMemo(
    () => (enabled && data && data.groups) || [],
    [enabled, data]
  );

  // Flatten the grouped results so arrow navigation can run across the whole
  // list while the visual grouping is preserved on render.
  const flat = useMemo(() => {
    const out = [];
    for (const group of groups) {
      for (const item of group.items) out.push({ ...item, groupType: group.type });
    }
    return out;
  }, [groups]);

  // Clamp the highlight to the current result set without a state-syncing
  // effect: the index is reset to 0 as the query changes (see onChange).
  const safeActive = flat.length ? Math.min(activeIndex, flat.length - 1) : 0;

  const close = useCallback(() => {
    setOpen(false);
    setTerm('');
    setActiveIndex(0);
  }, []);

  const go = useCallback((item) => {
    if (!item) return;
    navigate(resolveHref(item.entity, basePath));
    close();
  }, [navigate, basePath, close]);

  // Global Cmd/Ctrl+K toggles the palette from anywhere in the shell.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus the field when the overlay opens.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[safeActive]);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
          variant === 'sidebar'
            ? 'w-full border-sidebar-border bg-sidebar-elevated text-sidebar-ink-muted hover:text-sidebar-ink'
            : 'border-border bg-surface text-ink-muted hover:text-ink'
        )}
        aria-label="Cerca ovunque"
      >
        <Search className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
        <span className={variant === 'sidebar' ? 'flex-1 text-left' : 'hidden sm:inline'}>Cerca ovunque</span>
        <kbd
          className={clsx(
            'items-center gap-0.5 rounded border px-1 text-[10px] font-medium',
            variant === 'sidebar'
              ? 'inline-flex border-sidebar-border text-sidebar-ink-muted'
              : 'hidden border-border bg-surface-subtle text-ink-faint sm:inline-flex'
          )}
        >
          ⌘K
        </kbd>
      </button>

      {open && createPortal(
        // Rendered into document.body so the overlay escapes any ancestor that
        // establishes a containing block for fixed elements (e.g. the employee
        // sidebar uses a CSS transform). Without the portal the palette would
        // be clipped inside that ancestor instead of covering the viewport.
        <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
          <button
            type="button"
            aria-label="Chiudi ricerca"
            onClick={close}
            className="fixed inset-0 bg-black/40"
          />
          <div className="animate-zoom-in relative z-10 w-full max-w-xl overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated">
            <div className="flex items-center gap-2.5 border-b border-border px-3.5 [&_:focus-visible]:outline-none">
              {isFetching
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ink-faint" strokeWidth={1.75} />
                : <Search className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={1.75} />}
              <input
                ref={inputRef}
                value={term}
                onChange={(e) => { setTerm(e.target.value); setActiveIndex(0); }}
                onKeyDown={onInputKeyDown}
                placeholder="Cerca clienti, animali, prodotti, consulenze..."
                style={{ outline: 'none', boxShadow: 'none' }}
                className="h-12 flex-1 bg-transparent text-sm text-ink outline-none focus-visible:outline-none placeholder:text-ink-faint"
              />
              <kbd className="rounded border border-border bg-surface-subtle px-1.5 py-0.5 text-[10px] text-ink-faint">esc</kbd>
            </div>

            <div className="max-h-[55vh] overflow-y-auto py-1.5">
              {enabled && flat.length === 0 && !isFetching && (
                <p className="px-4 py-6 text-center text-xs text-ink-faint">Nessun risultato per "{debounced}".</p>
              )}
              {!enabled && (
                <p className="px-4 py-6 text-center text-xs text-ink-faint">Digita almeno 2 caratteri per cercare ovunque.</p>
              )}

              {groups.map((group) => {
                const Icon = TYPE_ICON[group.type] || Search;
                return (
                  <div key={group.type} className="mb-1">
                    <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-faint">
                      {group.label}
                    </p>
                    <ul>
                      {group.items.map((item) => {
                        const idx = flat.findIndex((f) => f.groupType === group.type && f.id === item.id);
                        const active = idx === safeActive;
                        const isOwn = item.entity && item.entity.storeId && ownStores.has(String(item.entity.storeId));
                        return (
                          <li key={`${group.type}-${item.id}`}>
                            <button
                              type="button"
                              onMouseEnter={() => setActiveIndex(idx)}
                              onClick={() => go({ ...item, groupType: group.type })}
                              className={clsx(
                                'flex w-full items-center gap-3 px-4 py-2 text-left',
                                active ? 'bg-surface-subtle' : 'hover:bg-surface-subtle'
                              )}
                            >
                              <Icon className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm text-ink">{item.title}</span>
                                {item.subtitle && (
                                  <span className="block truncate text-xs text-ink-muted">{item.subtitle}</span>
                                )}
                              </span>
                              {item.tag && (
                                <span
                                  className={clsx(
                                    'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                                    isOwn ? 'bg-brand/10 text-brand' : 'bg-surface-subtle text-ink-muted'
                                  )}
                                >
                                  {isOwn ? `${item.tag} · tuo` : item.tag}
                                </span>
                              )}
                              {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-ink-faint" strokeWidth={1.75} />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
